import React, { useEffect, useMemo, useState } from "react";
import Classes from "../AddClient.module.css";

export default function Step2VehicleInfo({ draft, setVehicle, back, finish }) {
    // Primary (required) vehicle still lives on draft.vehicle
    const v = draft?.vehicle || { make: "", model: "", license: "" };

    // Up to 3 *additional* vehicles (besides the primary one)
    const initialAdditional =
        (v.additionalVehicles && Array.isArray(v.additionalVehicles) && v.additionalVehicles) || [];

    const [additional, setAdditional] = useState(initialAdditional);

    // keep local state in sync if parent draft changes (e.g., user goes back/forward)
    useEffect(() => {
        const next =
            (draft?.vehicle?.additionalVehicles &&
                Array.isArray(draft.vehicle.additionalVehicles) &&
                draft.vehicle.additionalVehicles) ||
            [];
        setAdditional(next);
    }, [draft?.vehicle?.additionalVehicles]);

    const MAX_ADDITIONAL = 3;
    const canAddMore = additional.length < MAX_ADDITIONAL;
    const countLabel = useMemo(
        () => `Additional Vehicle(s) — optional (up to ${MAX_ADDITIONAL})`,
        []
    );

    const onChangePrimary = (e) => {
        const { name, value } = e.target;
        setVehicle({ [name]: value });
    };

    const addAdditional = () => {
        if (!canAddMore) return;
        const next = [...additional, { make: "", model: "", license: "" }];
        setAdditional(next);
        // store on the same "vehicle" object so parent keeps everything together
        setVehicle({ additionalVehicles: next });
    };

    const updateAdditional = (idx, field, value) => {
        const next = additional.map((item, i) => (i === idx ? { ...item, [field]: value } : item));
        setAdditional(next);
        setVehicle({ additionalVehicles: next });
    };

    const removeAdditional = (idx) => {
        const next = additional.filter((_, i) => i !== idx);
        setAdditional(next);
        setVehicle({ additionalVehicles: next });
    };

    return (
        <form
            className={Classes.infoCon}
            onSubmit={(e) => {
                e.preventDefault();
                // add any validation you need here (e.g., require primary fields)
                finish();
            }}
        >
            <div className={Classes.infoConMiddle}>
                <h3 style={{ marginBottom: 20 }}>Vehicle Info</h3>

                {/* Primary vehicle */}
                <div className={Classes.flexColGroup}>
                    <div className={Classes.flexCol1}>
                        <h5 className={Classes.label}>Vehicle Make</h5>
                        <input
                            className={Classes.inputField}
                            name="make"
                            value={v.make || ""}
                            onChange={onChangePrimary}
                            placeholder="e.g. Ford"
                        />
                    </div>

                    <div className={Classes.flexCol1}>
                        <h5 className={Classes.label}>Vehicle Model</h5>
                        <input
                            className={Classes.inputField}
                            name="model"
                            value={v.model || ""}
                            onChange={onChangePrimary}
                            placeholder="e.g. F-150"
                        />
                    </div>
                </div>

                <div className={Classes.flexColGroup}>
                    <div className={Classes.flexCol1}>
                        <h5 className={Classes.label}>License Plate</h5>
                        <input
                            className={Classes.inputField}
                            name="license"
                            value={v.license || ""}
                            onChange={onChangePrimary}
                            placeholder="Plate"
                        />
                    </div>
                </div>

                {additional.map((item, idx) => (
                    <div
                        key={idx}
                        style={{
                            border: "1px solid #e5e7eb",
                            borderRadius: 10,
                            padding: 12,
                            marginBottom: 12,
                            background: "#fff",
                            boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                        }}
                    >
                        <div className={Classes.flexBetween} style={{ marginBottom: 10 }}>
                            <h5 className={Classes.label} style={{ color: "#374151" }}>
                                Vehicle #{idx + 2}
                            </h5>
                            <button
                                type="button"
                                onClick={() => removeAdditional(idx)}
                                style={{
                                    border: "none",
                                    background: "rgba(241,138,58,0.1)",
                                    color: "#F18A3A",
                                    padding: "6px 10px",
                                    borderRadius: 8,
                                    cursor: "pointer",
                                    fontWeight: 600,
                                }}
                                aria-label={`Remove additional vehicle ${idx + 2}`}
                            >
                                Remove
                            </button>
                        </div>

                        <div className={Classes.flexColGroup}>
                            <div className={Classes.flexCol1}>
                                <h5 className={Classes.label}>Vehicle Make</h5>
                                <input
                                    className={Classes.inputField}
                                    value={item.make}
                                    onChange={(e) => updateAdditional(idx, "make", e.target.value)}
                                    placeholder="e.g. Toyota"
                                />
                            </div>

                            <div className={Classes.flexCol1}>
                                <h5 className={Classes.label}>Vehicle Model</h5>
                                <input
                                    className={Classes.inputField}
                                    value={item.model}
                                    onChange={(e) => updateAdditional(idx, "model", e.target.value)}
                                    placeholder="e.g. Camry"
                                />
                            </div>
                        </div>

                        <div className={Classes.flexColGroup}>
                            <div className={Classes.flexCol1}>
                                <h5 className={Classes.label}>License Plate</h5>
                                <input
                                    className={Classes.inputField}
                                    value={item.license}
                                    onChange={(e) => updateAdditional(idx, "license", e.target.value)}
                                    placeholder="Plate"
                                />
                            </div>
                        </div>
                    </div>
                ))}

                {/* Additional vehicles */}
                <div style={{ marginTop: 24, marginBottom: 8 }} className={Classes.flexBetween}>
                    <button
                        type="button"
                        onClick={addAdditional}
                        disabled={!canAddMore}
                        style={{
                            border: "none",
                            background: "transparent",
                            color: canAddMore ? "var(--primary-color)" : "#c2c2c2",
                            fontWeight: 600,
                            cursor: canAddMore ? "pointer" : "not-allowed",
                        }}
                    >
                        + Add Additional Vehicle
                    </button>
                </div>

                {!canAddMore && (
                    <div style={{ marginTop: 6, fontSize: 12, color: "#9aa3ae" }}>
                        You’ve reached the maximum of {MAX_ADDITIONAL} additional vehicles.
                    </div>
                )}
            </div>

            <div className={Classes.navRow}>
                <button type="button" className={Classes.backBtn} onClick={back}>
                    <h5>Back</h5>
                </button>
                <button type="submit" className={Classes.saveBtn}>
                    <h5>Finish</h5>
                </button>
            </div>
        </form>
    );
}
